<template>
  <div class="mandala-evaluation-preview p-4">
    <div class="row justify-content-between m-0 pb-4">
      <div class="mandala-evaluation-preview__info">
        <div class="mandala-evaluation-preview__logo">
          <img
            src="@/assets/images/logo.png"
            class="mandala-evaluation-preview__logo-icon">
          <img
            src="@/assets/images/today_text.svg"
            class="mandala-evaluation-preview__logo-text">
        </div>
        <div class="mandala-evaluation-preview__export-date">
          [出力日時] {{ exportDate }}
        </div>
        <div class="mandala-evaluation-preview__user-name">
          [出力者] {{ user.name }}
        </div>
      </div>
      <div class="mandala-evaluation-preview__title">
        {{ selectedYearMonthJpStr }} 月間目標自己評価チャート
      </div>
      <div class="mandala-evaluation-preview__right">
        <div class="mandala-evaluation-preview__buttons">
          <MiniButton
            class="mb-3"
            @click="$router.push({ name: 'mandalaEvaluationsExport' })">
            メンバー選択に戻る
          </MiniButton>
          <IconTextButton
            class="mb-3"
            active
            icon="home"
            @click="$router.push('/')">
            ホーム
          </IconTextButton>
        </div>
        <ChartLegends :legends="legends" />
      </div>
    </div>
    <div class="mandala-evaluation-preview__items row">
      <MandalaEvaluationPreviewItem
        v-for="member in members"
        :ref="member.id"
        :key="member.id"
        :user="member"
        :selected="prevRouteMemberId === member.id"
        :mandala-evaluations="mandalaEvaluationsByUserId[member.id] || []"
        :mandala-goal-template="getMandalaGoalTemplate(member.id)"
        :year-months="latest3YearMonths"
        :like-ids="likeIds"
        @click="showMandalaEvaluationDialog(member.id)" />
    </div>
    <div class="mandala-evaluation-preview__buttons">
      <MiniButton
        class="mb-3"
        @click="$router.push({ name: 'mandalaEvaluationsExport' })">
        メンバー選択に戻る
      </MiniButton>
      <IconTextButton
        class="mb-3"
        active
        icon="home"
        @click="$router.push('/')">
        ホーム
      </IconTextButton>
    </div>
    <PreviewMandalaEvaluationModal
      v-if="selectedMember"
      v-model="isShowMandalaEvaluationDialog"
      :is-manager="isManager"
      :user="selectedMember"
      :year-months="latest3YearMonths"
      :like-ids="likeIds"
      :mandala-evaluations="mandalaEvaluationsByUserId[selectedMember.id] || []"
      :mandala-goal-template="getMandalaGoalTemplate(selectedMember.id)" />
  </div>
</template>

<script>
import { addMonths, format, startOfMonth } from 'date-fns'
import { mapGetters, mapState } from 'vuex'
import MandalaEvaluationPreviewItem from '@/components/view/mandala_evaluations_export/MandalaEvaluationPreviewItem.vue'
import MiniButton from '@/components/parts/MiniButton.vue'
import IconTextButton from '@/components/parts/IconTextButton.vue'
import ChartLegends from '@/components/charts/ChartLegends.vue'
import PreviewMandalaEvaluationModal from '@/components/view/PreviewMandalaEvaluationModal.vue'
export default {
  components: {
    MandalaEvaluationPreviewItem,
    MiniButton,
    IconTextButton,
    ChartLegends,
    PreviewMandalaEvaluationModal
  },
  data() {
    return {
      members: [],
      mandalaEvaluations: [],
      likeIds: [],
      mandalaGoalTemplates: [],
      selectedMemberId: null,
      isShowMandalaEvaluationDialog: false
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isManager']),
    ...mapState('exportMandalaEvaluations', ['selectedMemberIds']),
    prevRouteMemberId() {
      return this.$route.query.prevRouteMemberId
    },
    colors() {
      return [
        '#E91E63',
        '#F48FB1',
        '#BDBDBD',
      ]
    },
    legends() {
      return this.latest3YearMonthLabels.map((label, index) => ({ label, color: this.colors[index] }))
    },
    exportDate() {
      return format(new Date(), 'yyyy/M/d H:mm')
    },
    accessibleDepartments() {
      if (!this.user) return []
      return this.user.accessibleDepartments
    },
    accessibleDepartmentIds() {
      return this.accessibleDepartments.map(department => department.id)
    },
    selectedYearMonthDate() {
      const yearMonth = this.$route.query.yearMonth
      return startOfMonth(yearMonth ? new Date(yearMonth) : new Date())
    },
    selectedYearMonth() {
      return this.latest3YearMonths[0]
    },
    selectedYearMonthJpStr() {
      return format(this.selectedYearMonthDate, 'yyyy年M月')
    },
    selectedMember() {
      return this.members.find(member => member.id === this.selectedMemberId) || null
    },
    latest3YearMonthDates() {
      return [
        this.selectedYearMonthDate,
        startOfMonth(addMonths(this.selectedYearMonthDate, -1)),
        startOfMonth(addMonths(this.selectedYearMonthDate, -2)),
      ]
    },
    latest3YearMonthLabels() {
      return this.latest3YearMonthDates.map(date => format(date, 'yyyy年M月'))
    },
    latest3YearMonths() {
      return this.latest3YearMonthDates.map(date => format(date, 'yyyy-MM-dd'))
    },
    latest3YearMonthStr() {
      return this.latest3YearMonths.join(',')
    },
    mandalaEvaluationsByUserId() {
      return this.mandalaEvaluations.reduce((prev, curr) => {
        if (curr.user.id in prev) {
          return { ...prev, [curr.user.id]: [...prev[curr.user.id], curr] }
        }
        return { ...prev, [curr.user.id]: [curr] }
      }, {})
    },
    mandalaGoalTemplateById() {
      return this.mandalaGoalTemplates.reduce((prev, curr) => {
        return { ...prev, [curr.id]: curr }
      }, {})
    }
  },
  async created() {
    this.$store.commit('startLoading')
    await Promise.all([
      this.fetchMandalaEvaluations(),
      this.fetchMembers(),
      this.fetchLikeIdsToMandalaEvaluations(),
      this.fetchMandalaGoalTemplates()
    ])

    if (this.prevRouteMemberId) {
      this.scrollToRef(this.prevRouteMemberId)
    }
    this.$store.commit('finishLoading')
  },
  methods: {
    //getで取得しようとすると、クエリパラメータが長すぎてエラーが発生するので、postで取得するように修正
    fetchMandalaEvaluations() {
      return this.$axios.post('/mandala_goal_self_evaluations/fetch_by_post/', {
        yearMonths: this.latest3YearMonths,
        userIds: this.selectedMemberIds,
      }).then(res => {
        this.mandalaEvaluations = res.data
      })
    },
    fetchMembers() {
      return this.$axios.post('/users/fetch_by_post/', {
        userIds: this.selectedMemberIds,
      }).then(res => {
        this.members = res.data
      })
    },
    fetchLikeIdsToMandalaEvaluations() {
      return this.$axios.get('/mandala_goal_self_evaluations/like_ids/', {
        params: {
          yearMonth: this.selectedYearMonth,
          limit: 999999
        }
      }).then(res => {
        this.likeIds = res.data.results
      })
    },
    fetchMandalaGoalTemplates() {
      return this.$axios.get('/mandala_goal_templates/', {
        params: {
          orderBy: 'display_order'
        }
      }).then(res => {
        this.mandalaGoalTemplates = res.data.results
      })
    },
    getMandalaGoalTemplate(userId) {
      const mandalaEvaluation = this.mandalaEvaluationsByUserId[userId]?.[0]
      if (!mandalaEvaluation) {
        return null
      }
      return this.mandalaGoalTemplateById[mandalaEvaluation.mandalaGoalTemplate]
    },
    showMandalaEvaluationDialog(memberId) {
      this.isShowMandalaEvaluationDialog = true
      this.selectedMemberId = memberId
    },
    scrollToRef(refId) {
      this.$nextTick(() => {
        const ref = Array.isArray(this.$refs[refId]) ? this.$refs[refId][0] : this.$refs[refId]
        if (ref) {
          this.scrollToEl(ref.$el)
        }
      })
    },
    scrollToEl(el) {
      const rect = el.getBoundingClientRect()
      const elementTop = rect.top + window.pageYOffset
      const offset = 20
      window.scrollTo(0, elementTop - offset)
    },
  }
}
</script>

<style lang="scss">
@page {
  size: A4 portrait;
}
canvas {
  min-height: 100%;
  max-width: 100%;
  max-height: 100%;
  height: auto!important;
  width: auto!important;
}
</style>

<style lang="scss" scoped>
.mandala-evaluation-preview {
  background-color: white;
  min-height: 100vh;

  &__right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  &__logo {
    display: flex;
    align-items: center;
    &-icon {
      height: 50px;
    }
    &-text {
      height: 30px;
    }
  }

  &__export-date, &__user-name {
    color: $grey05;
  }

  &__title {
    font-size: $fs-lg;
    font-weight: bold;
    padding-bottom: 12px;
  }
}
</style>